import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, Eye, EyeOff, RefreshCw, Search, X } from 'lucide-react';
import { logDataModels } from '../dataModelsLogger';

export interface ApiKeyCatalogOption {
  id: string;
  name: string;
  spaceName: string;
}

export interface ApiKeyDraft {
  description: string;
  catalogIds: string[];
  apiKey: string;
}

function randomToken(size: number): string {
  const buffer = new Uint8Array(size);
  crypto.getRandomValues(buffer);
  let out = '';
  for (let index = 0; index < buffer.length; index += 1) {
    out += buffer[index].toString(16).padStart(2, '0');
  }
  return out;
}

function issueKey(): string {
  return `sk_live_${randomToken(16)}`;
}

function issueSecret(): string {
  return `sec_${randomToken(24)}`;
}

export default function ApiKeysDialog({
  isLight,
  catalogs,
  initialCatalogIds,
  onClose,
  onCreate,
}: {
  isLight: boolean;
  catalogs: ApiKeyCatalogOption[];
  initialCatalogIds: string[];
  onClose: () => void;
  onCreate: (draft: ApiKeyDraft) => void;
}) {
  const [description, setDescription] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>(initialCatalogIds);
  const [query, setQuery] = useState('');
  const [apiKey, setApiKey] = useState(issueKey);
  const [secret, setSecret] = useState(issueSecret);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState<'key' | 'secret' | null>(null);
  const [issued, setIssued] = useState(false);
  const [catalogError, setCatalogError] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const needle = query.trim().toLowerCase();
  const visible = catalogs.filter((item) => {
    if (!needle) return true;
    return (item.name + ' ' + item.spaceName).toLowerCase().indexOf(needle) !== -1;
  });
  const groups: { spaceName: string; items: ApiKeyCatalogOption[] }[] = [];
  visible.forEach((item) => {
    const group = groups.find((entry) => entry.spaceName === item.spaceName);
    if (group) group.items.push(item);
    else groups.push({ spaceName: item.spaceName, items: [item] });
  });

  const toggle = (id: string) => {
    setCatalogError(false);
    setSelectedIds((current) => (current.indexOf(id) === -1 ? current.concat(id) : current.filter((item) => item !== id)));
  };

  const copyValue = async (kind: 'key' | 'secret', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      logDataModels('app', kind === 'key' ? 'api_key_copied' : 'api_secret_copied', { apiKeyPrefix: apiKey.slice(0, 12) });
      window.setTimeout(() => setCopied((current) => (current === kind ? null : current)), 1600);
    } catch (error) {
      logDataModels('error', 'api_credential_copy_failed', { kind, message: error instanceof Error ? error.message : 'copy failed' });
    }
  };

  const submit = () => {
    if (!selectedIds.length) {
      setCatalogError(true);
      logDataModels('warning', 'api_key_blocked', { reason: 'no_catalogs' });
      return;
    }
    logDataModels('app', 'api_key_issued', {
      apiKeyPrefix: apiKey.slice(0, 12),
      catalogIds: selectedIds,
      descriptionLength: description.trim().length,
    });
    setIssued(true);
    onCreate({ description: description.trim(), catalogIds: selectedIds, apiKey });
  };

  const panel = isLight ? 'border-slate-200 bg-white' : 'border-indigo-400/20 bg-black';
  const muted = isLight ? 'text-slate-500' : 'text-slate-400';
  const title = isLight ? 'text-slate-900' : 'text-white';
  const line = isLight ? 'border-slate-200' : 'border-indigo-400/15';
  const field = isLight
    ? 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
    : 'border-indigo-400/25 bg-black text-slate-100 placeholder:text-slate-500';

  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-3 md:p-6">
      <button type="button" className="absolute inset-0 bg-[#0F172A]/40" aria-label="Close" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="api-keys-title" className={`relative flex max-h-[92vh] w-full max-w-[560px] flex-col overflow-hidden rounded-lg border shadow-xl ${panel}`}>
        <header className={`flex items-start justify-between gap-3 border-b px-4 py-3 ${line}`}>
          <div className="min-w-0">
            <h2 id="api-keys-title" className={`text-[15px] font-semibold ${title}`}>API keys</h2>
            <p className={`mt-0.5 text-[13px] ${muted}`}>Issue a key and choose which data models it can access.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={`rounded-lg p-1.5 ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-slate-300 hover:bg-white/10'}`}>
            <X size={18} />
          </button>
        </header>

        <form
          className="min-h-0 flex-1 overflow-y-auto px-5 py-5"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label className="block">
            <span className={`mb-1.5 block text-sm font-medium ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>Description</span>
            <textarea
              value={description}
              maxLength={300}
              rows={3}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What this key is used for"
              className={`w-full resize-none rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${field}`}
            />
            <span className={`mt-1 block text-right text-xs ${muted}`}>{description.length}/300</span>
          </label>

          <div className="mt-4">
            <div className="flex items-center justify-between gap-3">
              <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>
                Data models <span className="text-rose-500">*</span>
              </span>
              <span className={`text-xs ${muted}`}>{selectedIds.length} selected</span>
            </div>
            <div className="relative mt-2">
              <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search data models..."
                className={`h-9 w-full rounded-lg border pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${field}`}
              />
            </div>
            <div className={`mt-2 max-h-52 overflow-y-auto rounded-xl border ${catalogError ? 'border-rose-400' : line}`}>
              {groups.length ? groups.map((group) => (
                <div key={group.spaceName} className={`border-b last:border-b-0 ${line}`}>
                  <p className={`px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide ${muted}`}>{group.spaceName}</p>
                  {group.items.map((item) => {
                    const checked = selectedIds.indexOf(item.id) !== -1;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-pressed={checked}
                        onClick={() => toggle(item.id)}
                        className={`flex w-full items-center gap-3 px-3 py-2 text-left ${checked ? (isLight ? 'bg-blue-50' : 'bg-blue-500/10') : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`}
                      >
                        <span className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border ${checked ? 'border-blue-600 bg-blue-600 text-white' : isLight ? 'border-slate-300 bg-white' : 'border-slate-500'}`}>
                          {checked ? <Check size={12} strokeWidth={3} /> : null}
                        </span>
                        <span className="min-w-0">
                          <span className={`block truncate text-sm font-medium ${title}`}>{item.name}</span>
                          <span className={`block truncate text-xs ${muted}`}>Data model</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )) : <p className={`px-3 py-6 text-center text-sm ${muted}`}>No data models match that search.</p>}
            </div>
            {catalogError ? <p className="mt-1.5 text-xs text-rose-600">Select at least one data model.</p> : null}
          </div>

          <div className="mt-5 space-y-3">
            <CredentialField
              label="API key"
              value={apiKey}
              revealed
              copied={copied === 'key'}
              isLight={isLight}
              onCopy={() => copyValue('key', apiKey)}
              onRefresh={() => {
                setApiKey(issueKey());
                setIssued(false);
                logDataModels('app', 'api_key_regenerated');
              }}
            />
            <CredentialField
              label="Secret"
              value={secret}
              revealed={showSecret}
              copied={copied === 'secret'}
              isLight={isLight}
              onCopy={() => copyValue('secret', secret)}
              onToggle={() => setShowSecret((value) => !value)}
              onRefresh={() => {
                setSecret(issueSecret());
                setShowSecret(true);
                setIssued(false);
                logDataModels('app', 'api_secret_regenerated');
              }}
            />
          </div>

          <p className={`mt-3 rounded-md border px-3 py-2.5 text-sm ${isLight ? 'border-[#E6E8EB] bg-[#F7F8FA] text-[#3C4048]' : 'border-white/10 bg-white/5 text-slate-300'}`}>
            {issued
              ? 'Key issued. Copy the secret before you close this dialog. It will not be shown again.'
              : 'The secret is shown only while this dialog is open. Copy it before you leave.'}
          </p>
        </form>

        <footer className={`flex justify-end gap-2 border-t px-5 py-4 ${line}`}>
          <button type="button" onClick={onClose} className={`h-9 rounded-lg border px-4 text-sm font-medium ${isLight ? 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50' : 'border-indigo-400/25 text-slate-200 hover:bg-white/5'}`}>
            {issued ? 'Done' : 'Cancel'}
          </button>
          {issued ? null : (
            <button type="button" onClick={submit} className="h-8 rounded-md bg-[#4B5563] px-3 text-[13px] font-medium text-white hover:bg-[#3C4048]">
              Create API key
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function CredentialField({
  label,
  value,
  revealed,
  copied,
  isLight,
  onCopy,
  onRefresh,
  onToggle,
}: {
  label: string;
  value: string;
  revealed: boolean;
  copied: boolean;
  isLight: boolean;
  onCopy: () => void;
  onRefresh: () => void;
  onToggle?: () => void;
}) {
  const muted = isLight ? 'text-slate-500' : 'text-slate-400';
  const field = isLight ? 'border-slate-200 bg-slate-50 text-slate-800' : 'border-indigo-400/20 bg-black text-slate-100';
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>{label}</span>
        <button type="button" onClick={onRefresh} className={`inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700`}>
          <RefreshCw size={12} /> Regenerate
        </button>
      </div>
      <div className={`flex items-center gap-1 rounded-lg border px-2 ${field}`}>
        <input readOnly value={revealed ? value : '•'.repeat(28)} className="h-10 min-w-0 flex-1 bg-transparent font-mono text-[13px] outline-none" />
        {onToggle ? (
          <button type="button" aria-label={revealed ? 'Hide secret' : 'Show secret'} onClick={onToggle} className={`rounded-md p-1.5 ${muted}`}>
            {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        ) : null}
        <button type="button" aria-label={`Copy ${label}`} onClick={onCopy} className={`rounded-md p-1.5 ${copied ? 'text-emerald-600' : muted}`}>
          {copied ? <Check size={15} /> : <Copy size={15} />}
        </button>
      </div>
    </div>
  );
}
